/**
 * invoiceFormat — the pure formatters every printed invoice shares.
 *
 * Extracted when a second A4 layout was added. Amount-in-words especially:
 * an invoice total is read aloud and disputed in words, and two templates
 * quietly disagreeing about how to spell ₹4,107 is the kind of defect nobody
 * notices until a buyer refuses the bill.
 */

export const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const inr = (n: number): string =>
  '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Bare number for table cells that already sit under a labelled money column. */
export const num = (n: number): string =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
  'eighteen', 'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

const twoDigits = (n: number): string =>
  n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? '-' + ONES[n % 10] : ''}`;

const wordsBelowThousand = (n: number): string => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return [h ? `${ONES[h]} hundred` : '', rest ? twoDigits(rest) : ''].filter(Boolean).join(' ');
};

/**
 * Indian numbering — crore, lakh, thousand. An invoice total is read aloud and
 * disputed in words, so it is spelled the way an Indian ledger spells it, not
 * as "one million".
 */
export const amountInWords = (amount: number): string => {
  const rupees = Math.floor(Math.abs(amount));
  const paise = Math.round((Math.abs(amount) - rupees) * 100);

  const parts: string[] = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const below = rupees % 1000;

  if (crore) parts.push(`${wordsBelowThousand(crore)} crore`);
  if (lakh) parts.push(`${wordsBelowThousand(lakh)} lakh`);
  if (thousand) parts.push(`${wordsBelowThousand(thousand)} thousand`);
  if (below) parts.push(wordsBelowThousand(below));

  const rupeeWords = parts.length ? parts.join(' ') : 'zero';
  const sign = amount < 0 ? 'minus ' : '';
  const tail = paise ? ` and ${twoDigits(paise)} paise` : '';
  const sentence = `${sign}rupees ${rupeeWords}${tail} only`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

/**
 * "INR Four Thousand One Hundred Seven Only" — the title-cased, INR-prefixed
 * form the classic GST layout prints, as distinct from the sentence form above.
 */
export const amountInWordsINR = (amount: number): string => {
  const sentence = amountInWords(amount)
    .replace(/^Rupees /, '')
    .replace(/^Minus rupees /, 'Minus ');
  const titled = sentence
    .split(' ')
    .map((w) => w.split('-').map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p)).join('-'))
    .join(' ');
  return `INR ${titled}`;
};

/** "Tamil Nadu, Code : 33" — the state, plus the code the buyer reconciles against. */
export const stateWithCode = (name?: string, code?: string): string =>
  name ? (code ? `${name} (${code})` : name) : code ? `Code ${code}` : '';
